import type { ReactNode } from 'react';
import './Sparkline.css';

export interface SparklineProps {
  /** Values in time order; the last value is the current one. */
  values: readonly number[];
  width?: number;
  height?: number;
  /** Fixed range; defaults to the data range so a flat line stays visible. */
  min?: number;
  max?: number;
  tone?: 'accent' | 'success' | 'warning' | 'danger' | 'muted';
  /** Spoken summary, e.g. "Accuracy 94% up from 88% over 7 days". */
  label: string;
}

export function sparklinePath(
  values: readonly number[],
  width: number,
  height: number,
  range?: { min: number; max: number },
): string {
  if (values.length === 0) return '';
  const lo = range?.min ?? Math.min(...values);
  const hi = range?.max ?? Math.max(...values);
  const span = hi - lo || 1;
  const pad = 1.5;
  const stepX = values.length > 1 ? (width - pad * 2) / (values.length - 1) : 0;
  return values
    .map((v, i) => {
      const x = pad + i * stepX;
      const y = pad + (height - pad * 2) * (1 - (v - lo) / span);
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
}

/** A tiny inline SVG trend, sized in CSS pixels, with a text alternative. */
export function Sparkline({
  values,
  width = 64,
  height = 20,
  min,
  max,
  tone = 'accent',
  label,
}: SparklineProps): ReactNode {
  const range = min !== undefined && max !== undefined ? { min, max } : undefined;
  const d = sparklinePath(values, width, height, range);
  const last = values[values.length - 1];
  const lastPoint = d ? d.split(' ').slice(-2) : null;
  return (
    <svg
      className={`pui-sparkline pui-sparkline--${tone}`}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={label}
    >
      <path d={d} fill="none" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      {lastPoint && last !== undefined ? (
        <circle cx={lastPoint[0]?.replace('L', '')} cy={lastPoint[1]} r={2} />
      ) : null}
    </svg>
  );
}
